import axios from 'axios';

// Voltron 지갑 서비스 API
const VOLTRON_WALLET_API = 'http://localhost:3600/api';
const VOLTRON_BACKEND_API = 'http://localhost:3600/api';

export interface VoltronWallet {
  id: string;
  walletAddress: string;
  nickname?: string;
  email?: string;
  phoneNumber?: string;
  tier: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | 'DIAMOND';
  vttnBalance: number;
  usdtBalance: number;
  stakingAmount: number;
  nodeSubscriptions: number;
  totalInvested: number;
  totalEarned: number;
  referralCode: string;
  isActive: boolean;
  createdAt: string;
  lastActivity: string;
  kycStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  investorType: 'INDIVIDUAL' | 'INSTITUTIONAL' | 'FOUNDATION';
}

export interface TransactionRecord {
  id: string;
  walletAddress: string;
  type: 'STAKING' | 'SWAP' | 'NODE_SUBSCRIPTION' | 'BOOM_EVENT' | 'AIRDROP' | 'BULK_TRANSFER' | 'GAS_FEE' | 'STAKING_REWARD' | 'WITHDRAWAL' | 'DEPOSIT' | 'PAYMENT';
  amount: number;
  currency: 'VTTN' | 'USDT' | 'ETH' | 'BTC';
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  txHash?: string;
  blockNumber?: number;
  gasUsed?: number;
  gasFee?: number;
  description: string;
  metadata?: any;
  createdAt: string;
  completedAt?: string;
}

export interface SettlementData {
  totalInflow: number;
  totalOutflow: number;
  netFlow: number;
  stakingRewards: number;
  nodeRevenue: number;
  gasFeesCollected: number;
  burnedTokens: number;
  airdropDistributed: number;
  foundationBalance: number;
  investorPayments: number;
  period: string;
}

interface WalletInfo {
  address: string;
  balance: {
    vttn: number;
    usdt: number;
  };
  tier: string;
  isConnected: boolean;
}

// 볼트론 지갑 실시간 연동 서비스
class VoltronWalletService {
  private walletAddress: string | null = null;
  private provider: any = null;
  private isConnected: boolean = false;

  constructor() {
    this.initializeWallet();
  }

  // 지갑 초기화
  private async initializeWallet() {
    if (typeof window !== 'undefined' && window.ethereum) {
      this.provider = window.ethereum;
      await this.checkConnection();
    }
  }

  // 지갑 연결 확인
  private async checkConnection() {
    try {
      const accounts = await this.provider.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        this.walletAddress = accounts[0];
        this.isConnected = true;
      }
    } catch (error) {
      console.error('지갑 연결 확인 실패:', error);
    }
  }

  // 지갑 연결
  async connectWallet(): Promise<string | null> {
    try {
      if (!this.provider) {
        throw new Error('볼트론 지갑이 설치되지 않았습니다.');
      }

      const accounts = await this.provider.request({ method: 'eth_requestAccounts' });
      if (accounts.length > 0) {
        this.walletAddress = accounts[0];
        this.isConnected = true;
        return this.walletAddress;
      }
      return null;
    } catch (error) {
      console.error('지갑 연결 실패:', error);
      throw error;
    }
  }

  // 기존 메서드들과 호환성을 위한 메서드
  async connectWalletCompat(): Promise<WalletInfo | null> {
    try {
      // Mock wallet connection
      return {
        address: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
        balance: {
          vttn: 1250.67,
          usdt: 850.32
        },
        tier: 'Gold',
        isConnected: true
      };
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      return null;
    }
  }

  async getBalanceCompat(address: string): Promise<{ vttn: number; usdt: number } | null> {
    try {
      const response = await axios.get(`${VOLTRON_WALLET_API}/wallet/${address}/balance`);
      return response.data;
    } catch (error) {
      console.error('Failed to get balance:', error);
      return { vttn: 1250.67, usdt: 850.32 };
    }
  }

  async sendTransactionCompat(to: string, amount: number, currency: 'vttn' | 'usdt'): Promise<string | null> {
    try {
      const response = await axios.post(`${VOLTRON_WALLET_API}/wallet/send`, {
        to,
        amount,
        currency
      });
      return response.data.txHash;
    } catch (error) {
      console.error('Failed to send transaction:', error);
      return null;
    }
  }

  // 새로운 메서드들
  // 모든 지갑 리스트 조회
  async getAllWallets(filters?: {
    tier?: string;
    isActive?: boolean;
    kycStatus?: string;
    investorType?: string;
    minBalance?: number;
    search?: string;
  }): Promise<VoltronWallet[]> {
    try {
      const response = await axios.get(`${VOLTRON_WALLET_API}/wallets`, {
        params: filters
      });
      return response.data.wallets || [];
    } catch (error) {
      console.error('Failed to fetch wallets:', error);
      // Mock data for development
      return this.getMockWallets();
    }
  }

  // 특정 지갑 정보 조회
  async getWalletInfo(walletAddress: string): Promise<VoltronWallet | null> {
    try {
      const response = await axios.get(`${VOLTRON_WALLET_API}/wallets/${walletAddress}`);
      return response.data.wallet;
    } catch (error) {
      console.error('Failed to fetch wallet info:', error);
      return null;
    }
  }

  // 지갑별 거래 내역 조회
  async getWalletTransactions(walletAddress: string, filters?: {
    type?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<TransactionRecord[]> {
    try {
      const response = await axios.get(`${VOLTRON_WALLET_API}/wallets/${walletAddress}/transactions`, {
        params: filters
      });
      return response.data.transactions || [];
    } catch (error) {
      console.error('Failed to fetch wallet transactions:', error);
      return this.getMockTransactions();
    }
  }

  // 전체 거래 내역 조회 (정산용)
  async getAllTransactions(filters?: {
    type?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<TransactionRecord[]> {
    try {
      const response = await axios.get(`${VOLTRON_BACKEND_API}/transactions`, {
        params: filters
      });
      return response.data.transactions || [];
    } catch (error) {
      console.error('Failed to fetch all transactions:', error);
      return this.getMockTransactions();
    }
  }

  // 정산 데이터 조회
  async getSettlementData(period: string): Promise<SettlementData> {
    try {
      const response = await axios.get(`${VOLTRON_BACKEND_API}/settlement`, {
        params: { period }
      });
      return response.data.settlement;
    } catch (error) {
      console.error('Failed to fetch settlement data:', error);
      return this.getMockSettlementData();
    }
  }

  // 보너스 지급
  async distributeBonus(data: {
    recipients: string[];
    amount: number;
    currency: 'VTTN' | 'USDT';
    reason: string;
    vestingMonths?: number;
  }): Promise<{ success: boolean; txHash?: string; error?: string }> {
    try {
      const response = await axios.post(`${VOLTRON_BACKEND_API}/bonus/distribute`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to distribute bonus:', error);
      return { success: false, error: 'Distribution failed' };
    }
  }

  // 대량 토큰 전송
  async bulkTransfer(data: {
    recipients: { address: string; amount: number }[];
    currency: 'VTTN' | 'USDT';
    reason: string;
  }): Promise<{ success: boolean; txHash?: string; error?: string }> {
    try {
      const response = await axios.post(`${VOLTRON_BACKEND_API}/tokens/bulk-transfer`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to bulk transfer:', error);
      return { success: false, error: 'Bulk transfer failed' };
    }
  }

  // 에어드롭 실행
  async executeAirdrop(data: {
    recipients: string[];
    amount: number;
    currency: 'VTTN' | 'USDT';
    vestingMonths?: number;
    eligibilityCriteria?: any;
  }): Promise<{ success: boolean; txHash?: string; error?: string }> {
    try {
      const response = await axios.post(`${VOLTRON_BACKEND_API}/airdrop/execute`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to execute airdrop:', error);
      return { success: false, error: 'Airdrop failed' };
    }
  }

  // 스테이킹 이자 지급
  async distributeStakingRewards(data: {
    stakeholders: { address: string; reward: number }[];
    period: string;
  }): Promise<{ success: boolean; txHash?: string; error?: string }> {
    try {
      const response = await axios.post(`${VOLTRON_BACKEND_API}/staking/distribute-rewards`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to distribute staking rewards:', error);
      return { success: false, error: 'Staking reward distribution failed' };
    }
  }

  // 가스비 지급
  async distributeGasFees(data: {
    validators: { address: string; amount: number }[];
    totalGasCollected: number;
  }): Promise<{ success: boolean; txHash?: string; error?: string }> {
    try {
      const response = await axios.post(`${VOLTRON_BACKEND_API}/gas/distribute`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to distribute gas fees:', error);
      return { success: false, error: 'Gas fee distribution failed' };
    }
  }

  // Mock 데이터 (개발용)
  private getMockWallets(): VoltronWallet[] {
    return [
      {
        id: '1',
        walletAddress: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C1',
        nickname: '투자자A',
        email: 'investor.a@voltron.io',
        tier: 'PLATINUM',
        vttnBalance: 150000,
        usdtBalance: 25000,
        stakingAmount: 100000,
        nodeSubscriptions: 3,
        totalInvested: 50000,
        totalEarned: 12500,
        referralCode: 'VTTN001',
        isActive: true,
        createdAt: '2024-01-15T10:30:00Z',
        lastActivity: '2024-01-20T15:45:00Z',
        kycStatus: 'VERIFIED',
        investorType: 'INSTITUTIONAL'
      },
      {
        id: '2',
        walletAddress: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
        nickname: '투자자B',
        email: 'investor.b@voltron.io',
        tier: 'GOLD',
        vttnBalance: 75000,
        usdtBalance: 15000,
        stakingAmount: 50000,
        nodeSubscriptions: 1,
        totalInvested: 25000,
        totalEarned: 6250,
        referralCode: 'VTTN002',
        isActive: true,
        createdAt: '2024-01-16T11:30:00Z',
        lastActivity: '2024-01-20T14:30:00Z',
        kycStatus: 'VERIFIED',
        investorType: 'INDIVIDUAL'
      },
      {
        id: '3',
        walletAddress: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C3',
        nickname: 'Voltron Foundation',
        email: 'foundation@voltron.io',
        tier: 'DIAMOND',
        vttnBalance: 500000000,
        usdtBalance: 1000000,
        stakingAmount: 0,
        nodeSubscriptions: 0,
        totalInvested: 0,
        totalEarned: 0,
        referralCode: 'FOUNDATION',
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z',
        lastActivity: '2024-01-20T16:00:00Z',
        kycStatus: 'VERIFIED',
        investorType: 'FOUNDATION'
      }
    ];
  }

  private getMockTransactions(): TransactionRecord[] {
    return [
      {
        id: '1',
        walletAddress: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C1',
        type: 'STAKING',
        amount: 10000,
        currency: 'VTTN',
        status: 'COMPLETED',
        txHash: '0x1234567890abcdef',
        blockNumber: 12345,
        gasUsed: 21000,
        gasFee: 0.001,
        description: '스테이킹 예치',
        createdAt: '2024-01-20T10:00:00Z',
        completedAt: '2024-01-20T10:01:00Z'
      },
      {
        id: '2',
        walletAddress: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
        type: 'NODE_SUBSCRIPTION',
        amount: 299,
        currency: 'USDT',
        status: 'COMPLETED',
        txHash: '0xabcdef1234567890',
        blockNumber: 12346,
        gasUsed: 35000,
        gasFee: 0.002,
        description: '노드 구독료 결제',
        createdAt: '2024-01-20T11:00:00Z',
        completedAt: '2024-01-20T11:02:00Z'
      }
    ];
  }

  private getMockSettlementData(): SettlementData {
    return {
      totalInflow: 1250000,
      totalOutflow: 850000,
      netFlow: 400000,
      stakingRewards: 125000,
      nodeRevenue: 450000,
      gasFeesCollected: 25000,
      burnedTokens: 50000,
      airdropDistributed: 100000,
      foundationBalance: 500000000,
      investorPayments: 675000,
      period: '2024-01'
    };
  }

  // 현재 연결된 지갑 주소 반환
  getWalletAddress(): string | null {
    return this.walletAddress;
  }

  // 연결 상태 확인
  isWalletConnected(): boolean {
    return this.isConnected && this.walletAddress !== null;
  }

  // 지갑 등록
  async registerWallet(walletData: {
    wallet_address: string;
    nickname: string;
    phone_number: string;
    pin: string;
    referral_code?: string;
  }): Promise<any> {
    try {
      const response = await fetch('http://localhost:3600/api/voltron-wallet/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(walletData)
      });

      if (!response.ok) {
        throw new Error('지갑 등록 실패');
      }

      const result = await response.json();
      if (result.success) {
        this.walletAddress = walletData.wallet_address;
        this.isConnected = true;
      }
      
      return result;
    } catch (error) {
      console.error('지갑 등록 실패:', error);
      throw error;
    }
  }

  // 지갑 로그인
  async loginWallet(wallet_address: string, pin: string): Promise<any> {
    try {
      const response = await fetch('http://localhost:3600/api/voltron-wallet/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wallet_address, pin })
      });

      if (!response.ok) {
        throw new Error('로그인 실패');
      }

      const result = await response.json();
      if (result.success) {
        this.walletAddress = wallet_address;
        this.isConnected = true;
      }
      
      return result;
    } catch (error) {
      console.error('로그인 실패:', error);
      throw error;
    }
  }

  // 지갑 잔액 조회
  async getBalance(): Promise<string> {
    try {
      if (!this.isConnected || !this.walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      const balance = await this.provider.request({
        method: 'eth_getBalance',
        params: [this.walletAddress, 'latest']
      });

      // Wei를 Ether로 변환
      const ethBalance = parseInt(balance, 16) / Math.pow(10, 18);
      return ethBalance.toFixed(6);
    } catch (error) {
      console.error('잔액 조회 실패:', error);
      throw error;
    }
  }

  // 토큰 잔액 조회 (ERC-20)
  async getTokenBalance(tokenAddress: string, decimals: number = 18): Promise<string> {
    try {
      if (!this.isConnected || !this.walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      // ERC-20 balanceOf 함수 호출
      const data = `0x70a08231000000000000000000000000${this.walletAddress.slice(2)}`;
      
      const result = await this.provider.request({
        method: 'eth_call',
        params: [{
          to: tokenAddress,
          data: data
        }, 'latest']
      });

      const balance = parseInt(result, 16) / Math.pow(10, decimals);
      return balance.toFixed(6);
    } catch (error) {
      console.error('토큰 잔액 조회 실패:', error);
      throw error;
    }
  }

  // 트랜잭션 전송
  async sendTransaction(to: string, value: string, data?: string): Promise<string> {
    try {
      if (!this.isConnected || !this.walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      const txParams = {
        from: this.walletAddress,
        to: to,
        value: `0x${(parseFloat(value) * Math.pow(10, 18)).toString(16)}`,
        data: data || '0x'
      };

      const txHash = await this.provider.request({
        method: 'eth_sendTransaction',
        params: [txParams]
      });

      return txHash;
    } catch (error) {
      console.error('트랜잭션 전송 실패:', error);
      throw error;
    }
  }

  // 스마트 컨트랙트 함수 호출
  async callContract(contractAddress: string, data: string): Promise<any> {
    try {
      if (!this.isConnected || !this.walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      const result = await this.provider.request({
        method: 'eth_call',
        params: [{
          from: this.walletAddress,
          to: contractAddress,
          data: data
        }, 'latest']
      });

      return result;
    } catch (error) {
      console.error('컨트랙트 호출 실패:', error);
      throw error;
    }
  }

  // 사용자 자산 정보 조회
  async getUserAssets(): Promise<any[]> {
    try {
      if (!this.isConnected || !this.walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      // 주요 토큰들의 잔액 조회
      const tokens = [
        { symbol: 'VTTN', address: process.env.REACT_APP_VTTN_TOKEN_ADDRESS, decimals: 18 },
        { symbol: 'USDT', address: process.env.REACT_APP_USDT_TOKEN_ADDRESS, decimals: 6 },
        { symbol: 'USDC', address: process.env.REACT_APP_USDC_TOKEN_ADDRESS, decimals: 6 }
      ];

      const assets = [];
      
      // ETH 잔액
      const ethBalance = await this.getBalance();
      assets.push({
        symbol: 'ETH',
        balance: ethBalance,
        address: 'native',
        decimals: 18
      });

      // 토큰 잔액들
      for (const token of tokens) {
        if (token.address) {
          try {
            const balance = await this.getTokenBalance(token.address, token.decimals);
            assets.push({
              symbol: token.symbol,
              balance: balance,
              address: token.address,
              decimals: token.decimals
            });
          } catch (error) {
            console.warn(`${token.symbol} 잔액 조회 실패:`, error);
          }
        }
      }

      return assets;
    } catch (error) {
      console.error('자산 정보 조회 실패:', error);
      throw error;
    }
  }

  // 트랜잭션 히스토리 조회
  async getTransactionHistory(limit: number = 10): Promise<any[]> {
    try {
      if (!this.isConnected || !this.walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      // 실제 구현에서는 블록체인 API나 인덱서를 사용
      const response = await fetch(`http://localhost:3600/api/voltron-wallet/transactions/${this.walletAddress}?limit=${limit}`);
      if (!response.ok) {
        throw new Error('트랜잭션 히스토리 조회 실패');
      }

      return await response.json();
    } catch (error) {
      console.error('트랜잭션 히스토리 조회 실패:', error);
      throw error;
    }
  }

  // 스테이킹 정보 조회
  async getStakingInfo(): Promise<any> {
    try {
      if (!this.isConnected || !this.walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      const response = await fetch(`http://localhost:3600/api/voltron-wallet/staking/${this.walletAddress}`);
      if (!response.ok) {
        throw new Error('스테이킹 정보 조회 실패');
      }

      return await response.json();
    } catch (error) {
      console.error('스테이킹 정보 조회 실패:', error);
      throw error;
    }
  }

  // 지갑 연결 해제
  disconnect() {
    this.walletAddress = null;
    this.isConnected = false;
  }

  // 이벤트 리스너 등록
  onAccountsChanged(callback: (accounts: string[]) => void) {
    if (this.provider) {
      this.provider.on('accountsChanged', callback);
    }
  }

  onChainChanged(callback: (chainId: string) => void) {
    if (this.provider) {
      this.provider.on('chainChanged', callback);
    }
  }
}

// 싱글톤 인스턴스 생성
const voltronWalletService = new VoltronWalletService();
export default voltronWalletService;

// 호환성을 위한 추가 export
export const walletService = voltronWalletService;

// 타입 정의
export interface WalletAsset {
  symbol: string;
  balance: string;
  address: string;
  decimals: number;
  value?: number;
}

export interface TransactionHistory {
  hash: string;
  from: string;
  to: string;
  value: string;
  timestamp: number;
  status: 'pending' | 'success' | 'failed';
  type: 'send' | 'receive' | 'contract';
}

export interface StakingInfo {
  totalStaked: string;
  rewards: string;
  apr: number;
  lockPeriod: number;
  canUnstake: boolean;
}